import React, { useState } from 'react';
import axios from './axiosConfig';
import { saveAs } from 'file-saver';
import { Packer } from 'docx';
import { buildTestDocument } from './utils/testDocument';
import GenerationLanguageToggle from './components/GenerationLanguageToggle';
import CurriculumSources from './components/CurriculumSources';
import { normalizeAiError } from './services/ai.js';
import { CAPS_GRADES, CAPS_SUBJECTS } from './utils/capsOptions.js';
import './AITestGenerator.css';

const INITIAL_FORM = { grade: '', subject: '', term: '1', topic: '', duration: '1 hour', totalMarks: 50, questionCount: 6, isLanguage: false, questionOneMultipleChoice: false, additionalContext: '', outputLanguage: 'English' };
const languageHint = 'CAPS language paper: Literal and understanding 40%, reorganisation and application 40%, evaluation and higher order 20%.';
const subjectHint = 'CAPS cognitive levels: Knowledge 30%, Routine procedures 40%, Complex procedures 20%, Problem solving 10%.';


export default function AITestGenerator() {
    const [form, setForm] = useState(INITIAL_FORM);
    const [test, setTest] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [topics, setTopics] = useState([]);
    const [topicsLoading, setTopicsLoading] = useState(false);
    const [topicNote, setTopicNote] = useState('');
    const [selectedTopicTitles, setSelectedTopicTitles] = useState([]);
    const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));

    const loadCapsTopics = async () => {
        if (!form.grade.trim() || !form.subject.trim()) {
            setError('Enter the grade and subject before loading CAPS topics.');
            return;
        }
        setError('');
        setTopicsLoading(true);
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/ai/topics.php`, { grade: form.grade, subject: form.subject, term: form.term }, { timeout: 180000 });
            setTopics(Array.isArray(response.data?.topics) ? response.data.topics : []);
            setSelectedTopicTitles([]);
            setTopicNote([response.data?.curriculumNotice, response.data?.teacherNote || 'Confirm the final scope against your school ATP.'].filter(Boolean).join(' '));
        } catch (requestError) {
            setError(normalizeAiError(requestError));
        } finally {
            setTopicsLoading(false);
        }
    };

    const toggleCapsTopic = (title) => {
        const next = selectedTopicTitles.includes(title)
            ? selectedTopicTitles.filter((item) => item !== title)
            : [...selectedTopicTitles, title];
        setSelectedTopicTitles(next);
        update('topic', next.join(', '));
    };

    const generateTest = async (event) => {
        event.preventDefault();
        setError('');
        setLoading(true);
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/ai/test.php`, form, { timeout: 180000 });
            if (response.headers['x-marksure-ai-fallback']) {
                setTest(null);
                setError('The AI service could not generate a usable test. Please retry in a moment. Do not use generic fallback content as an assessment.');
                return;
            }
            setTest({ ...response.data, outputLanguage: form.outputLanguage, term: form.term });
        } catch (requestError) {
            setError(normalizeAiError(requestError));
        } finally {
            setLoading(false);
        }
    };

    const downloadWord = async () => {
        if (!test) return;
        const document = buildTestDocument(test, form);
        const blob = await Packer.toBlob(document);
        const fileName = `${test.subject || form.subject}_Grade${test.grade || form.grade}_Test`.replace(/[^a-z0-9]+/gi, '_');
        saveAs(blob, `${fileName}.docx`);
    };

    return <main className="ai-test-page">
        <section className="ai-test-hero"><p>CAPS Assessment Studio</p><h1>Build a test/exam</h1><span>Generate a complete learner paper, memorandum and cognitive-level marking grid.</span></section>
        <section className="ai-test-builder">
            <div className="ai-test-heading"><div><p>Test brief</p><h2>Set the assessment requirements</h2></div><span>AI assisted</span></div>
            <form onSubmit={generateTest} className="ai-test-form">
                <GenerationLanguageToggle value={form.outputLanguage} onChange={(value) => update('outputLanguage', value)} disabled={loading} />
                <div className="ai-test-grid ai-test-grid--three"><label>Grade<select required value={form.grade} onChange={(event) => update('grade', event.target.value)}><option value="">Select grade</option>{!CAPS_GRADES.some((grade) => grade.value === form.grade) && form.grade && <option value={form.grade}>{form.grade}</option>}{CAPS_GRADES.map((grade) => <option key={grade.value} value={grade.value}>{grade.label}</option>)}</select></label><label>Subject<select required value={form.subject} onChange={(event) => update('subject', event.target.value)}><option value="">Select subject</option>{!CAPS_SUBJECTS.includes(form.subject) && form.subject && <option value={form.subject}>{form.subject}</option>}{CAPS_SUBJECTS.map((subject) => <option key={subject} value={subject}>{subject}</option>)}</select></label><label>Term<select value={form.term} onChange={(event) => update('term', event.target.value)}>{[1, 2, 3, 4].map((term) => <option key={term} value={term}>Term {term}</option>)}</select></label></div>
                <div className="ai-test-topic-field"><label>Topic or scope<input required value={form.topic} onChange={(event) => update('topic', event.target.value)} placeholder="e.g. Fractions, area and volume" /></label><button type="button" onClick={loadCapsTopics} disabled={topicsLoading}>{topicsLoading ? 'Finding topics...' : 'Get CAPS topics'}</button></div>
                {topics.length > 0 && <div className="ai-test-topic-results"><strong>Suggested Term {form.term} CAPS topics</strong><span className="ai-test-topic-count">{selectedTopicTitles.length} selected</span><div>{topics.map((item) => <button type="button" key={item.title} className={selectedTopicTitles.includes(item.title) ? 'selected' : ''} onClick={() => toggleCapsTopic(item.title)} aria-pressed={selectedTopicTitles.includes(item.title)}><span>{item.title}</span><small>{item.description}</small></button>)}</div>{topicNote && <p>{topicNote}</p>}</div>}
                <div className="ai-test-grid"><label>Duration<input value={form.duration} onChange={(event) => update('duration', event.target.value)} placeholder="e.g. 1 hour 30 minutes" /></label><label>Total marks<input type="number" min="10" max="200" value={form.totalMarks} onChange={(event) => update('totalMarks', Number(event.target.value) || 50)} /></label><label>Main questions<input type="number" min="3" max={Math.min(20, Math.max(3, form.totalMarks))} value={form.questionCount} onChange={(event) => update('questionCount', Number(event.target.value) || 6)} /></label></div>
                <label className="ai-test-toggle"><input type="checkbox" checked={form.isLanguage} onChange={(event) => update('isLanguage', event.target.checked)} /><span><strong>This is a language subject</strong><small>Creates comprehension, summary, and language structures sections.</small></span></label>
                <label className="ai-test-toggle"><input type="checkbox" checked={form.questionOneMultipleChoice} disabled={loading} onChange={(event) => update('questionOneMultipleChoice', event.target.checked)} /><span><strong>Make Question 1 multiple choice</strong><small>All Question 1 subquestions (1.1, 1.2, etc.) will have A–D choices, with answers in the memorandum.</small></span></label>
                <p className="ai-test-framework">{form.isLanguage ? languageHint : subjectHint}</p>
                <label>Teacher notes <span>(optional)</span><textarea rows="3" value={form.additionalContext} onChange={(event) => update('additionalContext', event.target.value)} placeholder="Include a source text, focus on the listed topics, use South African contexts..." /></label>
                <button className="ai-test-generate" disabled={loading}>{loading ? 'Generating test...' : 'Generate test package'}</button>
            </form>
            {error && <div className="ai-test-error">{error}</div>}
        </section>
        {test && <section className="ai-test-output">
            <div className="ai-test-output-head">
                <div>
                    <p>Ready to download</p>
                    <h2>{test.title}</h2>
                    <span>Grade {test.grade} | {test.subject} | {test.duration} | {test.totalMarks} marks | {test.outputLanguage}</span>
                </div>
                <button onClick={downloadWord}>Download Word document</button>
            </div>
            <CurriculumSources sources={test.curriculumSources} notice={test.curriculumNotice} />
        </section>}
    </main>;
}
